// Kalshi — implied odds, never counts. Reads an event ticker or per-candidate
// market tickers. Blank config → "not configured".
//
// Schema note (verified against the live API, 2026): prices come as *_dollars
// STRINGS in the 0..1 range (e.g. "0.9490" = 95%), NOT integer cents; the
// candidate name is in `yes_sub_title`; volume is `volume_fp`. Tickers are
// case-sensitive and canonically UPPERCASE. Legacy cents fields are still read
// as a fallback.
import type { MarketProvider } from '../../types/providers.ts';
import { ProviderError } from '../../types/providers.ts';
import type { MarketOutcome, MarketResult } from '../../types/markets.ts';
import type { CandidateConfig, RaceConfig } from '../../types/config.ts';
import { fetchJson } from '../http.ts';
import { nowIso } from '../../lib/time.ts';

interface KMarket {
  ticker?: string;
  title?: string;
  yes_sub_title?: string;
  event_ticker?: string;
  status?: string;
  // Current schema: dollar strings, 0..1.
  last_price_dollars?: string | number;
  yes_bid_dollars?: string | number;
  yes_ask_dollars?: string | number;
  volume_fp?: string | number;
  volume_24h_fp?: string | number;
  // Legacy schema: integer cents.
  last_price?: number;
  yes_bid?: number;
  yes_ask?: number;
  volume?: number;
}
interface KMarketsResp {
  markets?: KMarket[];
}

const CLOSED_STATUSES = new Set(['closed', 'settled', 'finalized', 'determined']);

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}
function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

/** Implied YES probability, 0..1. Prefers the dollar fields; falls back to
 *  legacy cents. */
function marketProb(m: KMarket): number | null {
  const lastD = num(m.last_price_dollars);
  const bidD = num(m.yes_bid_dollars);
  const askD = num(m.yes_ask_dollars);
  if (lastD != null && lastD > 0) return clamp01(lastD);
  if (bidD != null && askD != null && bidD + askD > 0) return clamp01((bidD + askD) / 2);
  if (bidD != null && bidD > 0) return clamp01(bidD);

  // Legacy cents.
  if (typeof m.last_price === 'number' && m.last_price > 0) return clamp01(m.last_price / 100);
  const b = typeof m.yes_bid === 'number' ? m.yes_bid : null;
  const a = typeof m.yes_ask === 'number' ? m.yes_ask : null;
  if (b != null && a != null && b + a > 0) return clamp01((b + a) / 200);
  if (b != null && b > 0) return clamp01(b / 100);
  return null;
}

function marketVolume(m: KMarket): number | null {
  return num(m.volume_fp) ?? num(m.volume_24h_fp) ?? (typeof m.volume === 'number' ? m.volume : null);
}

function matchCandidate(m: KMarket, race: RaceConfig): CandidateConfig | null {
  const mt = (m.ticker ?? '').toUpperCase();
  const byTicker = race.candidates.find(
    (c) => c.kalshiMarketTicker && c.kalshiMarketTicker.toUpperCase() === mt,
  );
  if (byTicker) return byTicker;
  const hay = `${m.yes_sub_title ?? ''} ${m.title ?? ''}`.toLowerCase();
  return race.candidates.find((c) => hay.includes(c.name.toLowerCase())) ?? null;
}

function toOutcome(m: KMarket, cand: CandidateConfig | null): MarketOutcome {
  return {
    candidateId: cand?.id ?? null,
    label: cand?.name ?? m.yes_sub_title ?? m.title ?? m.ticker ?? 'Outcome',
    probability: marketProb(m),
    volume: marketVolume(m),
  };
}

export const kalshiProvider: MarketProvider = {
  id: 'kalshi',
  kind: 'markets',
  async getMarket(race, cfg, signal) {
    const transport = cfg.transport ?? 'auto';
    const perCandidate = race.candidates
      .filter((c) => c.kalshiMarketTicker)
      .map((c) => (c.kalshiMarketTicker as string).toUpperCase());

    let path: string | null = null;
    if (cfg.eventTicker) {
      path = `markets?status=open&event_ticker=${encodeURIComponent(cfg.eventTicker.toUpperCase())}`;
    } else if (perCandidate.length) {
      path = `markets?tickers=${encodeURIComponent(perCandidate.join(','))}`;
    }
    if (!path) throw new ProviderError('not_configured', 'No Kalshi event ticker or market tickers configured');

    const resp = await fetchJson<KMarketsResp>({
      providerKey: 'kalshi',
      upstreamPath: path,
      transport,
      signal,
    });
    const markets = resp.markets ?? [];
    if (!markets.length) throw new ProviderError('not_configured', 'No Kalshi markets for the configured event/tickers');

    const all = markets.map((m) => toOutcome(m, matchCandidate(m, race)));
    // Prefer the race's tracked candidates; if none matched (e.g. a name
    // mismatch), fall back to showing every outcome rather than blanking.
    const mapped = all.filter((o) => o.candidateId != null);
    const outcomes = (mapped.length ? mapped : all).sort(
      (x, y) => (y.probability ?? -1) - (x.probability ?? -1),
    );

    return {
      source: 'Kalshi',
      sourceId: 'kalshi',
      fetchedAt: nowIso(),
      outcomes,
      closed: markets.every((m) => CLOSED_STATUSES.has(m.status ?? '')),
    } satisfies MarketResult;
  },
};
